import { Component, type ReactElement } from "react";
import { connect } from "react-redux";
import { type InjectedFormProps, reduxForm } from "redux-form";

import { type ISelectOption } from "../../../app-utils/form.js";
import { type IConfiguredAttribute } from "../../../app-utils/playbook.js";
import { getAttributeLabel } from "../../../redux/modules/sample-admin.js";
import { type IAppState } from "../../../redux/reducer.js";
import {
  ATTRIBUTE_TYPE,
  ATTRIBUTE_TYPE_OPTIONS,
  COLUMN_SET,
  WORKFLOW_PARAM_USAGE,
} from "../../../settings/settings.js";
import { Dialog } from "../../core/overlays/Dialogs.js";
import { ModalControlledOverlay } from "../../core/overlays/index.js";
import CheckboxField, { CheckboxReduxFormField } from "../../core/redux-form/CheckboxField.js";
import InputField, { InputReduxFormField } from "../../core/redux-form/InputField.js";
import ReactSelectField, { ReactSelectReduxFormField } from "../../core/redux-form/ReactSelectField.js";

interface IAttributeInputConfigurationDialogOwnProps {
  attribute: IConfiguredAttribute;
  allAttributes: IConfiguredAttribute[];
  onApply?: (attribute: IConfiguredAttribute) => void;
  onCancel?: () => void;
}

interface IAttributeInputConfigurationDialogAppStateProps {}

interface IAttributeInputConfigurationDialogDispatchProps {}

export interface IAttributeInputConfigurationDialogProps
  extends IAttributeInputConfigurationDialogAppStateProps,
    IAttributeInputConfigurationDialogDispatchProps,
    IAttributeInputConfigurationDialogOwnProps,
    InjectedFormProps<FormData, any> {}

function mapStateToProps(
  _state: IAppState,
  ownProps: IAttributeInputConfigurationDialogOwnProps,
): IAttributeInputConfigurationDialogAppStateProps {
  return {
    initialValues: {
      [AttributeInputConfigurationDialogImpl.ATTRIBUTE_INPUT_CONFIGURATION_DIALOG_FIELD_DESCRIPTION]:
        ownProps.attribute.description,
      [AttributeInputConfigurationDialogImpl.ATTRIBUTE_INPUT_CONFIGURATION_DIALOG_FIELD_LABEL]:
        ownProps.attribute.label,
      [AttributeInputConfigurationDialogImpl.ATTRIBUTE_INPUT_CONFIGURATION_DIALOG_FIELD_TYPE]:
        ATTRIBUTE_TYPE_OPTIONS.find((option: ISelectOption) => ownProps.attribute.correctedTypeId === option.value),
      [AttributeInputConfigurationDialogImpl.ATTRIBUTE_INPUT_CONFIGURATION_DIALOG_FIELD_MANDATORY]:
        ownProps.attribute.mandatory || ownProps.attribute.correctedMandatory,
    },
  };
}

function mapDispatchToProps(_dispatch): IAttributeInputConfigurationDialogDispatchProps {
  return {};
}

class AttributeInputConfigurationDialogImpl extends Component<IAttributeInputConfigurationDialogProps> {
  public static FORM_NAME = "ATTRIBUTE_INPUT_CONFIGURATION";

  public static ATTRIBUTE_INPUT_CONFIGURATION_DIALOG_FIELD_DESCRIPTION = "DESCRIPTION";
  public static ATTRIBUTE_INPUT_CONFIGURATION_DIALOG_FIELD_MANDATORY = "MANDATORY";
  public static ATTRIBUTE_INPUT_CONFIGURATION_DIALOG_FIELD_LABEL = "LABEL";
  public static ATTRIBUTE_INPUT_CONFIGURATION_DIALOG_FIELD_TYPE = "TYPE";

  constructor(props: IAttributeInputConfigurationDialogProps) {
    super(props);
  }

  handleSubmitData = (values) => {
    const attribute: IConfiguredAttribute = {
      ...this.props.attribute,
      description: values[AttributeInputConfigurationDialogImpl.ATTRIBUTE_INPUT_CONFIGURATION_DIALOG_FIELD_DESCRIPTION],
      label: values[AttributeInputConfigurationDialogImpl.ATTRIBUTE_INPUT_CONFIGURATION_DIALOG_FIELD_LABEL],
      correctedTypeId:
        values[AttributeInputConfigurationDialogImpl.ATTRIBUTE_INPUT_CONFIGURATION_DIALOG_FIELD_TYPE]?.value,
      correctedMandatory:
        values[AttributeInputConfigurationDialogImpl.ATTRIBUTE_INPUT_CONFIGURATION_DIALOG_FIELD_MANDATORY],
    };
    this.props.onApply(attribute);
  };

  validateLabelDuplicates = (value, _allValues, props: IAttributeInputConfigurationDialogProps) => {
    const duplicateLabelExists = props.allAttributes
      .filter((attribute: IConfiguredAttribute) => attribute.id !== props.attribute.id) // filter out itself
      .find((attribute: IConfiguredAttribute) => {
        const attributeLabel: string = attribute.label || attribute.attributeName;
        if (value === attributeLabel && attribute.groupName === props.attribute.groupName) {
          return true;
        }
      });
    if (duplicateLabelExists) {
      return "This label already exist within the same group";
    }
  };

  render(): ReactElement {
    return (
      <ModalControlledOverlay isOpen={true} title={"Modal"} close={this.props.onCancel}>
        {({ close }) => (
          <Dialog
            closeFn={close}
            mainActionButtonProps={{
              isLoading: this.props.submitting,
              isDisabled: this.props.submitting,
              children: "Apply",
              onPress: this.props.handleSubmit(this.handleSubmitData),
            }}
            headerProps={{
              title: getAttributeLabel(this.props.attribute),
              subtitle: " Playbook-specific attribute settings",
            }}
            footerMessage={{ message: this.props.error }}
          >
            <div>
              {/* sample level attributes are only configurable with respect to mandatoriness
              (grid display, therefore no label or description) */}
              {(this.props.attribute.isWorkflowParam ||
                this.props.attribute.columnSet.id === COLUMN_SET.COS_PARENT_ID.id) && (
                <>
                  <InputReduxFormField
                    tooltip={`Provide a custom label during data input`}
                    componentClass="input"
                    name={AttributeInputConfigurationDialogImpl.ATTRIBUTE_INPUT_CONFIGURATION_DIALOG_FIELD_LABEL}
                    label={"Label"}
                    placeholder={`Custom label`}
                    component={InputField}
                    validate={this.validateLabelDuplicates}
                  />

                  <InputReduxFormField
                    tooltip={`Provide a description or hint during data input`}
                    componentClass="input"
                    name={AttributeInputConfigurationDialogImpl.ATTRIBUTE_INPUT_CONFIGURATION_DIALOG_FIELD_DESCRIPTION}
                    label={"Description"}
                    placeholder={`Optional Description`}
                    component={InputField}
                  />
                </>
              )}
              {!this.props.attribute.isWorkflowParam && (
                <CheckboxReduxFormField
                  name={AttributeInputConfigurationDialogImpl.ATTRIBUTE_INPUT_CONFIGURATION_DIALOG_FIELD_MANDATORY}
                  label="Mandatory"
                  component={CheckboxField}
                  tooltipText={
                    this.props.attribute.mandatory
                      ? "This attribute is mandatory by default"
                      : "Check to make this attribute mandatory in this playbook"
                  }
                  disabled={this.props.attribute.mandatory}
                />
              )}
              {/* do not provide the input type correction for option lists and for everything with a known usage, like paths and files */}
              {this.props.attribute.isWorkflowParam &&
                this.props.attribute.attributeType.id !== ATTRIBUTE_TYPE.CONTROLLED_TEXT &&
                this.props.attribute.attributeType.id !== ATTRIBUTE_TYPE.BOOLEAN &&
                !(this.props.attribute.usage in WORKFLOW_PARAM_USAGE) && (
                  <ReactSelectReduxFormField
                    name={AttributeInputConfigurationDialogImpl.ATTRIBUTE_INPUT_CONFIGURATION_DIALOG_FIELD_TYPE}
                    isClearable={true}
                    isSearchable={true}
                    label={"Corrected input type"}
                    tooltip={"Use a different input field type..."}
                    placeholder={"Select field type..."}
                    component={ReactSelectField}
                    options={ATTRIBUTE_TYPE_OPTIONS.filter(
                      (typeOption: ISelectOption) =>
                        typeOption.value === ATTRIBUTE_TYPE.FREE_TEXT ||
                        typeOption.value === ATTRIBUTE_TYPE.FREE_NUMERICAL,
                    )}
                  />
                )}
            </div>
          </Dialog>
        )}
      </ModalControlledOverlay>
    );
  }
}

const AttributeInputConfigurationDialog = connect<
  IAttributeInputConfigurationDialogAppStateProps,
  IAttributeInputConfigurationDialogDispatchProps,
  IAttributeInputConfigurationDialogOwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(
  reduxForm({
    form: AttributeInputConfigurationDialogImpl.FORM_NAME,
    // validate: CreateProjectDialogImpl.validate, // we use field level validation
  })(AttributeInputConfigurationDialogImpl),
);

export { AttributeInputConfigurationDialog, AttributeInputConfigurationDialogImpl };
export default AttributeInputConfigurationDialog;
